import React, { useState, useEffect, useCallback } from 'react';
import './AnimatedDemo.css';

const RESULTS = [
  { sym: 'AAPL', q: 78, s: 62, cvx: 2.8, conv: 3.8, action: 'Neutral',
    tests: [
      { name: 'ROIC vs WACC', score: 88 },
      { name: 'FCF Yield', score: 82 },
      { name: 'Moat Strength', score: 91 },
      { name: 'Financial Health', score: 85 },
    ],
  },
  { sym: 'MSFT', q: 85, s: 71, cvx: 3.2, conv: 4.1, action: 'Favorable' },
  { sym: 'AMZN', q: 72, s: 58, cvx: 2.5, conv: 3.3, action: 'Neutral' },
];

export default function ScreenerDemo({ autoPlay = true, loop = true }) {
  const [phase, setPhase] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [visibleResults, setVisibleResults] = useState(0);
  const [barWidths, setBarWidths] = useState({});
  const [expandedSym, setExpandedSym] = useState(null);
  const [testBarWidths, setTestBarWidths] = useState({});
  const [cursorPos, setCursorPos] = useState({ x: 50, y: 30, visible: false, clicking: false });

  const tickers = 'AAPL, MSFT, AMZN';

  const runDemo = useCallback(() => {
    setPhase(0); setTypedText(''); setVisibleResults(0); setBarWidths({});
    setExpandedSym(null); setTestBarWidths({});
    setCursorPos({ x: 50, y: 30, visible: false, clicking: false });

    // Type tickers
    let charIdx = 0;
    const typeInterval = setInterval(() => {
      charIdx++;
      setTypedText(tickers.slice(0, charIdx));
      if (charIdx >= tickers.length) {
        clearInterval(typeInterval);
        setTimeout(() => setPhase(1), 600);
      }
    }, 60);

    // Move cursor to analyze button then click
    setTimeout(() => setCursorPos({ x: 260, y: 42, visible: true, clicking: false }), 1800);
    setTimeout(() => setCursorPos({ x: 260, y: 42, visible: true, clicking: true }), 2200);
    setTimeout(() => { setCursorPos(p => ({ ...p, visible: false })); setPhase(2); }, 2500);

    // Show results
    setTimeout(() => {
      let idx = 0;
      const showInterval = setInterval(() => {
        idx++;
        setVisibleResults(idx);
        const r = RESULTS[idx - 1];
        if (r) {
          setTimeout(() => {
            setBarWidths(prev => ({ ...prev, [r.sym]: true }));
          }, 200);
        }
        if (idx >= RESULTS.length) clearInterval(showInterval);
      }, 600);
    }, 3500);

    // After results shown, cursor clicks AAPL (first result) to expand test breakdown
    setTimeout(() => setCursorPos({ x: 40, y: 75, visible: true, clicking: false }), 6200);
    setTimeout(() => setCursorPos({ x: 40, y: 75, visible: true, clicking: true }), 6600);
    setTimeout(() => {
      setCursorPos(p => ({ ...p, visible: false }));
      setExpandedSym('AAPL');
      // Animate test bars after expand
      setTimeout(() => setTestBarWidths({ filled: true }), 200);
    }, 6900);
  }, []);

  useEffect(() => {
    if (!autoPlay) return;
    runDemo();
    if (!loop) return;
    const loopId = setInterval(runDemo, 16000);
    return () => clearInterval(loopId);
  }, [autoPlay, loop, runDemo]);

  const barColor = (v, max) => { const pct = (v / max) * 100; return pct >= 66 ? '#6495ED' : pct >= 33 ? '#FBBF24' : '#EF4444'; };

  return (
    <div className="demo-container">
      <div className="demo-titlebar">
        <span className="demo-dot red" /><span className="demo-dot yellow" /><span className="demo-dot green" />
        <span className="demo-titlebar-text">alphagrade.io/screener</span>
      </div>
      <div className="demo-body" style={{ position: 'relative' }}>
        {cursorPos.visible && (
          <svg className="demo-cursor-arrow" style={{ left: cursorPos.x, top: cursorPos.y, transform: cursorPos.clicking ? 'scale(0.85)' : 'scale(1)' }} viewBox="0 0 24 24" fill="white" stroke="black" strokeWidth="1">
            <path d="M5 3l14 8-6 2-4 6z" />
          </svg>
        )}
        {/* Input */}
        <div className="demo-input-row">
          <span className="demo-input-label">🔍</span>
          <span className="demo-input-text">{typedText}<span className="demo-cursor" /></span>
          <span className={`demo-btn ${phase >= 1 ? 'clicked' : ''}`}>
            {phase < 2 ? 'Analyze' : 'Scoring...'}
          </span>
        </div>

        {/* Results */}
        {RESULTS.slice(0, visibleResults).map((r) => (
          <div key={r.sym} style={{
            background: 'var(--bg-primary, #0C1220)', borderRadius: 10, padding: '0.85rem',
            marginBottom: '0.6rem', animation: 'slideInUp 0.3s ease both',
            border: '1px solid var(--border-subtle, #1E293B)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, color: 'var(--neon-green)', fontSize: '1rem' }}>{r.sym}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', fontWeight: 700, color: '#6495ED', padding: '0.15rem 0.5rem', background: 'rgba(100,149,237,0.12)', borderRadius: 4 }}>
                Conv: {r.conv}
              </span>
            </div>

            <div className="demo-score-row">
              <span className="demo-score-label">Quality</span>
              <div className="demo-score-track">
                <div className="demo-score-fill" style={{ width: barWidths[r.sym] ? `${r.q}%` : '0%', background: barColor(r.q, 100), transition: 'width 0.8s ease' }} />
              </div>
              <span className="demo-score-val">{r.q}</span>
            </div>

            <div className="demo-score-row">
              <span className="demo-score-label">Setup</span>
              <div className="demo-score-track">
                <div className="demo-score-fill" style={{ width: barWidths[r.sym] ? `${r.s}%` : '0%', background: barColor(r.s, 100), transition: 'width 0.8s ease 0.2s' }} />
              </div>
              <span className="demo-score-val">{r.s}</span>
            </div>

            <div className="demo-score-row">
              <span className="demo-score-label">Convex</span>
              <div className="demo-score-track">
                <div className="demo-score-fill" style={{ width: barWidths[r.sym] ? `${(r.cvx / 5) * 100}%` : '0%', background: barColor(r.cvx, 5), transition: 'width 0.8s ease 0.4s' }} />
              </div>
              <span className="demo-score-val">{r.cvx}</span>
            </div>

            <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Signal: <strong style={{ color: '#6495ED' }}>{r.action}</strong></span>
              {r.tests && (
                <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  {expandedSym === r.sym ? '▼' : '▶'} See test breakdown
                </span>
              )}
            </div>

            {/* Expanded test breakdown */}
            {expandedSym === r.sym && r.tests && (
              <div style={{
                marginTop: '0.6rem', paddingTop: '0.6rem',
                borderTop: '1px solid var(--border-subtle, #1E293B)',
                animation: 'slideInUp 0.25s ease both',
              }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--neon-green, #00D4AA)', fontWeight: 700, marginBottom: '0.45rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  Quality Test Breakdown
                </div>
                {r.tests.map((t) => (
                  <div key={t.name} className="demo-score-row" style={{ marginBottom: '0.25rem' }}>
                    <span className="demo-score-label" style={{ fontSize: '0.68rem', minWidth: 90 }}>{t.name}</span>
                    <div className="demo-score-track">
                      <div className="demo-score-fill" style={{
                        width: testBarWidths.filled ? `${t.score}%` : '0%',
                        background: t.score >= 66 ? '#6495ED' : t.score >= 33 ? '#FBBF24' : '#EF4444',
                        transition: 'width 0.6s ease',
                      }} />
                    </div>
                    <span className="demo-score-val" style={{ fontSize: '0.7rem' }}>{t.score}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
